import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import UnifiedSubscriptionManagement from '../components/Reports/UnifiedSubscriptionManagement';
import UnifiedSubscriptionForm from '../components/Reports/UnifiedSubscriptionForm';
import AlertsManagement from '../components/Reports/AlertsManagement';
import AISummaryManagement from '../components/Reports/AISummaryManagement';
import AIScheduledTasksManagement from '../components/Reports/AIScheduledTasksManagement';
import { Tabs } from '../components/ui/Tabs';

const SubscriptionsPage = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const { userSettings, currentOrganization } = useUserSettingsContext();
  const aiSummaryEnabled = useFeatureFlagEnabled('ai-summary');
  // AI scheduled tasks are created in the assistant, so the tab follows the
  // same gate as the Lodgeic AI surface itself.
  const aiTasksEnabled = useFeatureFlagEnabled('ai-assistant') !== false;

  // Determine initial tab from URL parameter
  const getInitialTab = () => {
    if (tab === 'ai-summary' && aiSummaryEnabled) return 'ai-summary';
    if (tab === 'ai-tasks' && aiTasksEnabled) return 'ai-tasks';
    if (tab === 'alerts') return 'alerts';
    return 'subscriptions';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [showForm, setShowForm] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Sync tab with URL when URL parameter changes
  useEffect(() => {
    const newTab = getInitialTab();
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [tab, aiSummaryEnabled, aiTasksEnabled]);

  // Update URL when tab changes (without adding to history for cleaner navigation)
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (newTab === 'subscriptions') {
      navigate('/subscriptions', { replace: true });
    } else {
      navigate(`/subscriptions/${newTab}`, { replace: true });
    }
  };

  const handleCreateNew = () => {
    setEditingSubscription(null);
    setShowForm(true);
  };

  const handleEdit = (subscription) => {
    setEditingSubscription(subscription);
    setShowForm(true);
  };

  const handleFormSave = (savedSubscription) => {
    setShowForm(false);
    setEditingSubscription(null);
    // Trigger refresh of the subscription list
    setRefreshTrigger(prev => prev + 1);

    // Show appropriate success message
    if (editingSubscription) {
      alert('Subscription updated successfully!');
    } else if (savedSubscription?._multiUser) {
      alert(`${savedSubscription._userCount} subscriptions created successfully (one per user)!`);
    } else {
      alert('Subscription created successfully!');
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingSubscription(null);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Tab Navigation */}
      <Tabs
        className="mb-6"
        value={activeTab}
        onChange={handleTabChange}
        tabs={[
          { key: 'subscriptions', label: 'Report Subscriptions' },
          ...(aiSummaryEnabled ? [{ key: 'ai-summary', label: 'AI Summary' }] : []),
          ...(aiTasksEnabled ? [{ key: 'ai-tasks', label: 'AI Scheduled Tasks' }] : []),
          { key: 'alerts', label: 'Alerts' },
        ]}
      />

      {/* Tab Content */}
      {activeTab === 'subscriptions' && (
        <UnifiedSubscriptionManagement
          currentUser={userSettings}
          currentOrganization={currentOrganization}
          onCreateNew={handleCreateNew}
          onEdit={handleEdit}
          refreshTrigger={refreshTrigger}
        />
      )}

      {activeTab === 'ai-summary' && aiSummaryEnabled && (
        <AISummaryManagement
          currentUser={userSettings}
          currentOrganization={currentOrganization}
          refreshTrigger={refreshTrigger}
        />
      )}

      {activeTab === 'ai-tasks' && aiTasksEnabled && (
        <AIScheduledTasksManagement
          currentUser={userSettings}
          refreshTrigger={refreshTrigger}
        />
      )}

      {activeTab === 'alerts' && (
        <AlertsManagement
          currentUser={userSettings}
          currentOrganization={currentOrganization}
          refreshTrigger={refreshTrigger}
        />
      )}

      {/* Subscription Form Modal */}
      {showForm && (
        <UnifiedSubscriptionForm
          subscription={editingSubscription}
          currentOrganization={currentOrganization}
          mode={editingSubscription ? 'edit' : 'create'}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}
    </div>
  );
};

export default SubscriptionsPage; 